import { useEffect, useRef, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import '@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css';
import * as turf from '@turf/turf';

interface UseMapboxDrawProps {
  getMapInstance: () => mapboxgl.Map | null | undefined;
  measureMode: 'line' | 'polyline' | 'area';
  onMeasureResult: (results: Array<{ distance?: number; area?: number; coordinates: number[][] }>) => void;
  isDrawing: boolean;
  distanceHasStarted: boolean;
  onDrawingChange: (drawing: boolean) => void;
  onDistanceStartedChange: (started: boolean) => void;
  onLiveMeasurement?: (value: number | null) => void;
}

export const useMapboxDraw = ({
  getMapInstance,
  measureMode,
  onMeasureResult,
  isDrawing,
  distanceHasStarted,
  onDrawingChange,
  onDistanceStartedChange,
  onLiveMeasurement
}: UseMapboxDrawProps) => {
  const drawRef = useRef<any>(null);
  const measureResultsRef = useRef<Array<{ distance?: number; area?: number; coordinates: number[][] }>>([]);
  const isInitializedRef = useRef(false);
  const liveMeasurementRef = useRef<number | null>(null);

  // Initialize MapboxDraw only once
  useEffect(() => {
    const map = getMapInstance();
    if (!map) return;

    // Clean up existing draw control if it exists
    if (drawRef.current && map) {
      try {
        map.removeControl(drawRef.current);
      } catch (error) {
        console.warn('Error removing existing draw control:', error);
      }
      drawRef.current = null;
      isInitializedRef.current = false;
    }

    // Create new draw instance
    drawRef.current = new MapboxDraw({
      displayControlsDefault: false,
      controls: {},
      styles: [
        {
          id: 'gl-draw-line',
          type: 'line',
          filter: ['all', ['==', '$type', 'LineString'], ['!=', 'mode', 'static']],
          layout: {
            'line-cap': 'round',
            'line-join': 'round'
          },
          paint: {
            'line-color': '#3bb2d0',
            'line-dasharray': [0.2, 2],
            'line-width': 3
          }
        },
        {
          id: 'gl-draw-line-static',
          type: 'line',
          filter: ['all', ['==', '$type', 'LineString'], ['==', 'mode', 'static']],
          layout: {
            'line-cap': 'round',
            'line-join': 'round'
          },
          paint: {
            'line-color': '#3bb2d0',
            'line-width': 3
          }
        },
        {
          id: 'gl-draw-polygon',
          type: 'fill',
          filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
          paint: {
            'fill-color': '#3bb2d0',
            'fill-outline-color': '#3bb2d0',
            'fill-opacity': 0.1
          }
        },
        {
          id: 'gl-draw-polygon-static',
          type: 'fill',
          filter: ['all', ['==', '$type', 'Polygon'], ['==', 'mode', 'static']],
          paint: {
            'fill-color': '#3bb2d0',
            'fill-outline-color': '#3bb2d0',
            'fill-opacity': 0.1
          }
        },
        {
          id: 'gl-draw-polygon-stroke',
          type: 'line',
          filter: ['all', ['==', '$type', 'Polygon'], ['!=', 'mode', 'static']],
          layout: {
            'line-cap': 'round',
            'line-join': 'round'
          },
          paint: {
            'line-color': '#3bb2d0',
            'line-dasharray': [0.2, 2],
            'line-width': 2
          }
        },
        {
          id: 'gl-draw-polygon-stroke-static',
          type: 'line',
          filter: ['all', ['==', '$type', 'Polygon'], ['==', 'mode', 'static']],
          layout: {
            'line-cap': 'round',
            'line-join': 'round'
          },
          paint: {
            'line-color': '#3bb2d0',
            'line-width': 2
          }
        },
        {
          id: 'gl-draw-point',
          type: 'circle',
          filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'vertex']],
          paint: {
            'circle-radius': 5,
            'circle-color': '#fff',
            'circle-stroke-color': '#3bb2d0',
            'circle-stroke-width': 2
          }
        },
        {
          id: 'gl-draw-point-stroke',
          type: 'circle',
          filter: ['all', ['==', '$type', 'Point'], ['==', 'meta', 'vertex']],
          paint: {
            'circle-radius': 5,
            'circle-color': '#fff',
            'circle-stroke-color': '#3bb2d0',
            'circle-stroke-width': 2
          }
        }
      ]
    });

    map.addControl(drawRef.current);
    isInitializedRef.current = true;

    return () => {
      if (drawRef.current && map) {
        try {
          map.removeControl(drawRef.current);
        } catch (error) {
          console.warn('Error removing draw control on cleanup:', error);
        }
        drawRef.current = null;
        isInitializedRef.current = false;
      }
    };
  }, [getMapInstance]);

  // Calculate live measurement
  const calculateLiveMeasurement = useCallback(() => {
    const draw = drawRef.current;
    if (!draw) return null;

    const data = draw.getAll();
    if (!data || data.features.length === 0) return null;

    const activeFeatures = data.features.filter((f: any) => f.properties && f.properties.active);
    if (activeFeatures.length === 0) return null;

    const activeFeature = activeFeatures[0];
    if (activeFeature.geometry.type === 'LineString') {
      const coordinates = activeFeature.geometry.coordinates;
      if (coordinates.length >= 2) {
        return calculateDistance(coordinates);
      }
    } else if (activeFeature.geometry.type === 'Polygon') {
      const coordinates = activeFeature.geometry.coordinates[0];
      if (coordinates.length >= 3) {
        return calculateArea(coordinates);
      }
    }

    return null;
  }, []);

  // Handle drawing events
  useEffect(() => {
    const map = getMapInstance();
    if (!map || !drawRef.current) return;

    const handleDrawCreate = (e: any) => {
      const data = drawRef.current?.getAll();
      if (!data) return;

      const results: Array<{ distance?: number; area?: number; coordinates: number[][] }> = [];

      data.features.forEach((feature: any) => {
        if (feature.geometry.type === 'LineString') {
          const coordinates = feature.geometry.coordinates;
          const distance = calculateDistance(coordinates);
          results.push({ distance, coordinates });
          
          // Add measurement label to the map
          addMeasurementLabel(map, coordinates, distance, false);
        } else if (feature.geometry.type === 'Polygon') {
          const coordinates = feature.geometry.coordinates[0];
          const area = calculateArea(coordinates);
          results.push({ area, coordinates });
          
          // Add measurement label to the map
          addMeasurementLabel(map, coordinates, area, true);
        }
      });

      measureResultsRef.current = results;
      onMeasureResult(results);
      
      // Don't change drawing state - let the drawing stay on the map
      // Only reset the live measurement
      liveMeasurementRef.current = null;
      onLiveMeasurement?.(null);
    };

    const handleDrawUpdate = (e: any) => {
      const data = drawRef.current?.getAll();
      if (!data) return;

      const results: Array<{ distance?: number; area?: number; coordinates: number[][] }> = [];

      data.features.forEach((feature: any) => {
        if (feature.geometry.type === 'LineString') {
          const coordinates = feature.geometry.coordinates;
          const distance = calculateDistance(coordinates);
          results.push({ distance, coordinates });
        } else if (feature.geometry.type === 'Polygon') {
          const coordinates = feature.geometry.coordinates[0];
          const area = calculateArea(coordinates);
          results.push({ area, coordinates });
        }
      });

      measureResultsRef.current = results;
      onMeasureResult(results);
    };

    const handleDrawDelete = () => {
      measureResultsRef.current = [];
      onMeasureResult([]);
      onDrawingChange(false);
      onDistanceStartedChange(false);
      liveMeasurementRef.current = null;
      onLiveMeasurement?.(null);
      
      // Remove measurement labels
      removeMeasurementLabels(map);
    };

    const handleDrawSelectionChange = (e: any) => {
      // Only change drawing state if no features are selected and we're not actively drawing
      if (e.features.length === 0 && !isDrawing) {
        onDrawingChange(false);
        onDistanceStartedChange(false);
      }
    };

    map.on('draw.create', handleDrawCreate);
    map.on('draw.update', handleDrawUpdate);
    map.on('draw.delete', handleDrawDelete);
    map.on('draw.selectionchange', handleDrawSelectionChange);

    return () => {
      map.off('draw.create', handleDrawCreate);
      map.off('draw.update', handleDrawUpdate);
      map.off('draw.delete', handleDrawDelete);
      map.off('draw.selectionchange', handleDrawSelectionChange);
    };
  }, [getMapInstance, onMeasureResult, onDrawingChange, onDistanceStartedChange, onLiveMeasurement]);

  // Handle mouse events for live measurement
  useEffect(() => {
    const map = getMapInstance();
    if (!map || !drawRef.current) return;

    const handleMouseMove = (e: mapboxgl.MapMouseEvent) => {
      // Only show live measurement when actively drawing AND distance has started
      if (!isDrawing || !distanceHasStarted) {
        if (liveMeasurementRef.current !== null) {
          liveMeasurementRef.current = null;
          onLiveMeasurement?.(null);
        }
        return;
      }

      const liveValue = calculateLiveMeasurement();
      if (liveValue !== liveMeasurementRef.current) {
        liveMeasurementRef.current = liveValue;
        onLiveMeasurement?.(liveValue);
      }
    };

    const handleDrawVertex = (e: any) => {
      // Set distanceHasStarted to true when the first vertex is added
      if (isDrawing && !distanceHasStarted) {
        onDistanceStartedChange(true);
      }

      // Calculate live measurement after vertex is added
      const liveValue = calculateLiveMeasurement();
      if (liveValue !== liveMeasurementRef.current) {
        liveMeasurementRef.current = liveValue;
        onLiveMeasurement?.(liveValue);
      }

      // For line mode, automatically finish after second point
      if (measureMode === 'line') {
        const data = drawRef.current?.getAll();
        if (!data) return;

        const activeFeatures = data.features.filter((f: any) => f.properties && f.properties.active);
        if (activeFeatures.length > 0) {
          const activeFeature = activeFeatures[0];
          if (activeFeature.geometry.type === 'LineString' && activeFeature.geometry.coordinates.length >= 2) {
            // Finish the line automatically
            drawRef.current?.changeMode('simple_select');
            onDrawingChange(false);
            onDistanceStartedChange(false);
            liveMeasurementRef.current = null;
            onLiveMeasurement?.(null);
          }
        }
      }
    };

    map.on('mousemove', handleMouseMove);
    map.on('draw.vertex', handleDrawVertex);

    return () => {
      map.off('mousemove', handleMouseMove);
      map.off('draw.vertex', handleDrawVertex);
    };
  }, [getMapInstance, isDrawing, distanceHasStarted, measureMode, onDrawingChange, onDistanceStartedChange, onLiveMeasurement, calculateLiveMeasurement]);

  // Start drawing function
  const startDrawing = useCallback(() => {
    const map = getMapInstance();
    if (!map || !drawRef.current) return;

    onDrawingChange(true);
    onDistanceStartedChange(false);
    liveMeasurementRef.current = null;
    onLiveMeasurement?.(null);

    if (measureMode === 'line') {
      drawRef.current.changeMode('draw_line_string');
    } else if (measureMode === 'polyline') {
      drawRef.current.changeMode('draw_line_string');
    } else if (measureMode === 'area') {
      drawRef.current.changeMode('draw_polygon');
    }
  }, [measureMode, onDrawingChange, onDistanceStartedChange, getMapInstance, onLiveMeasurement]);

  // Clear drawing function
  const clearDrawing = useCallback(() => {
    const map = getMapInstance();
    if (!map || !drawRef.current) return;

    drawRef.current.deleteAll();
    measureResultsRef.current = [];
    onMeasureResult([]);
    onDrawingChange(false);
    onDistanceStartedChange(false);
    liveMeasurementRef.current = null;
    onLiveMeasurement?.(null);
    
    // Remove measurement labels
    removeMeasurementLabels(map);
  }, [onMeasureResult, onDrawingChange, onDistanceStartedChange, getMapInstance, onLiveMeasurement]);

  return {
    startDrawing,
    clearDrawing,
    isDrawing,
    distanceHasStarted
  };
};

// Helper function to add measurement labels to the map
function addMeasurementLabel(map: mapboxgl.Map, coordinates: number[][], value: number, isArea: boolean) {
  const labelId = `measurement-label-${Date.now()}`;
  
  // Calculate center point for label placement
  let centerLng = 0, centerLat = 0;
  
  if (isArea) {
    // For polygons, calculate centroid
    let sumLng = 0, sumLat = 0;
    coordinates.forEach(([lng, lat]) => {
      sumLng += lng;
      sumLat += lat;
    });
    centerLng = sumLng / coordinates.length;
    centerLat = sumLat / coordinates.length;
  } else {
    // For lines, use midpoint
    const midIndex = Math.floor(coordinates.length / 2);
    centerLng = coordinates[midIndex][0];
    centerLat = coordinates[midIndex][1];
  }

  // Format the measurement value
  const formattedValue = isArea ? `${value.toFixed(2)} km²` : `${value.toFixed(2)} km`;

  // Add label source and layer
  map.addSource(labelId, {
    type: 'geojson',
    data: {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [centerLng, centerLat]
      },
      properties: {
        label: formattedValue
      }
    }
  });

  map.addLayer({
    id: labelId,
    type: 'symbol',
    source: labelId,
    layout: {
      'text-field': ['get', 'label'],
      'text-size': 12,
      'text-anchor': 'center',
      'text-offset': [0, 0],
      'text-allow-overlap': true
    },
    paint: {
      'text-color': '#3bb2d0',
      'text-halo-color': '#ffffff',
      'text-halo-width': 2
    }
  });
}

// Helper function to remove measurement labels
function removeMeasurementLabels(map: mapboxgl.Map) {
  const layers = map.getStyle().layers || [];
  layers.forEach(layer => {
    if (layer.id && layer.id.startsWith('measurement-label-')) {
      if (map.getLayer(layer.id)) {
        map.removeLayer(layer.id);
      }
      if (map.getSource(layer.id)) {
        map.removeSource(layer.id);
      }
    }
  });
}

// Helper functions
function calculateDistance(coordinates: number[][]): number {
  let distance = 0;
  for (let i = 1; i < coordinates.length; i++) {
    const [lng1, lat1] = coordinates[i - 1];
    const [lng2, lat2] = coordinates[i];
    distance += getDistance(lat1, lng1, lat2, lng2);
  }
  return distance;
}

function calculateArea(coordinates: number[][]): number {
  // Use turf.js for accurate area calculation
  if (coordinates.length < 3) return 0;
  
  // Create a polygon feature
  const polygon = turf.polygon([coordinates]);
  
  // Calculate area using turf.js area function (returns square meters)
  const areaInSquareMeters = turf.area(polygon);
  
  // Convert to square kilometers
  const areaInSquareKilometers = areaInSquareMeters / 1000000;
  
  return areaInSquareKilometers;
}

function getDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
